import './PurchaseAgreementStyle.less';

import {
  CurrencyInput,
  DatePicker,
  DropdownSelect,
  Icon,
  RadioButtonGroupInput,
  SingleLineInput,
} from '@opendoor/bricks';

import OrderedListItem from './OrderedListItem';

import moment from 'moment';
import React from 'react';

const MAX_HOA_FEES = 2;
const MAX_SIGNER_NAMES = 2;
const MIN_DAYS_TO_CLOSE = 21;
const MAX_DAYS_TO_CLOE = 60;
const MIN_DAYS_CLOSE_DATE = moment().add(MIN_DAYS_TO_CLOSE, 'd');
const MAX_DAYS_CLOSE_DATE = moment().add(MAX_DAYS_TO_CLOE, 'd');

interface IHoaFee {
  hoaFeeCents?: number;
  hoaFeeFrequency?: string;
}

export interface IProps {
  // form
  closeDate?: moment.Moment;
  hasHoa?: boolean;
  hoaFees?: IHoaFee[];
  hoaName?: string;
  signerNames?: string[];
  onUpdate: (key: string, value) => void;

  // init
  hasAcceptedInvitation?: boolean;
  maybeAcceptInvitation: () => void;
  onReturnClick: () => void;
  returnUrl: string;
  shouldAcceptInvitation?: boolean;
  streetAddress: string;
}

class PurchaseAgreement extends React.Component<IProps> {
  public static defaultProps: Partial<IProps> = {
    hoaFees: [{}], // initialize with at least one empty hoa fee
    signerNames: [''], // initialize with at least one empty name
  };

  public componentDidMount() {
    const { shouldAcceptInvitation, hasAcceptedInvitation, maybeAcceptInvitation } = this.props;
    if (shouldAcceptInvitation && !hasAcceptedInvitation) {
      maybeAcceptInvitation();
    }
  }

  public render() {
    const {
      closeDate,
      hasAcceptedInvitation,
      hasHoa,
      hoaFees,
      hoaName,
      onReturnClick,
      returnUrl,
      signerNames,
      streetAddress,
    } = this.props;

    if (!hasAcceptedInvitation) {
      return null;
    }

    const signerNameInputs = (
      <div>
        {signerNames.map((signerName, index) => (
          <div key={`signerNames-${index}`}>
            <h6 className="prose">
              {/* only supports 2 names for now */}
              {index === 0 ? 'Your Name' : "Second Owner's Name"}
            </h6>
            <div className="text-product-medium">As it should appear on the agreement</div>
            <SingleLineInput
              className="od-purchase-agreement__input"
              inputType="text"
              name={`signerNames-${index}`}
              onChange={this.onListItemUpdate}
              placeholder="Name"
              required={true}
              content={signerName}
            />
          </div>
        ))}
        {signerNames.length < MAX_SIGNER_NAMES ? (
          <div className="row">
            <div className="col-xs">
              <small>Anyone else need to sign, like a spouse?</small>
            </div>
            <div className="end-xs">
              <button
                className="button link small"
                onClick={this.addListInput.bind(null, 'signerNames', '')}
              >
                + Add Owner
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-xs">
              <small>Only need to have one person sign?</small>
            </div>
            <div className="end-xs">
              <button
                className="button link small"
                onClick={this.removeListInput.bind(null, 'signerNames')}
              >
                Remove second owner
              </button>
            </div>
          </div>
        )}
      </div>
    );

    const hoaInputs = hasHoa && (
      <div>
        <SingleLineInput
          className="od-purchase-agreement__input"
          content={hoaName}
          inputType="text"
          name="hoaName"
          onChange={this.props.onUpdate}
          required={true}
          title="HOA name"
        />
        {hoaFees.map((hoaFee, index) => (
          <div className="od-purchase-agreement__hoa-fee-group" key={`hoaFeeCents-${index}`}>
            <CurrencyInput
              className="od-purchase-agreement__input"
              content={hoaFee['hoaFeeCents']}
              name={`hoaFeeCents-${index}`}
              onChange={this.onListObjectUpdate.bind(null, 'hoaFees')}
              placeholder="$ ###"
              required={true}
              title="HOA fee amount"
            />
            <div className="od-purchase-agreement__dropdown">
              <DropdownSelect
                name={`hoaFeeFrequency-${index}`}
                onChange={this.onListObjectUpdate.bind(null, 'hoaFees', `hoaFeeFrequency-${index}`)}
                options={[
                  {
                    label: 'Monthly',
                    value: 'Monthly',
                  },
                  {
                    label: 'Quarterly',
                    value: 'Quarterly',
                  },
                  {
                    label: 'Semi Annually',
                    value: 'Semi-Annually',
                  },
                  {
                    label: 'Annually',
                    value: 'Annually',
                  },
                ]}
                selectedValue={hoaFee['hoaFeeFrequency']}
                title={'HOA fee frequency'}
              />
            </div>
          </div>
        ))}
        {hoaFees.length < MAX_HOA_FEES ? (
          <div className="row">
            <div className="col-6">
              <small>Are you a member of multiple HOAs?</small>
            </div>
            <div className="col-6 text-right">
              <button
                className="button link small"
                onClick={this.addListInput.bind(null, 'hoaFees', {})}
              >
                + Add another HOA
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-xs">
              <small>Are you a member of multiple HOAs?</small>
            </div>
            <div className="end-xs">
              <button
                className="button link small"
                onClick={this.removeListInput.bind(null, 'hoaFees')}
              >
                Remove secondary HOA fee
              </button>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <section className="container od-purchase-agreement">
        <div className="row">
          <div className="col-xs-12 col-sm-5">
            <span className="text-muted">Get a purchase agreement</span>
            <h1 className="big-headline prose">{streetAddress}</h1>
            <p>
              Confirm or edit your details listed here and Opendoor will work with our trusted buyer
              partner to prepare a purchase agreement for the sale of your home. You can then review
              and decide if you want to accept it.
            </p>
          </div>
          <div className="col-xs-12 col-sm-offset-1 col-sm-5">
            <div className="hidden-sm-up">
              <hr className="od-purchase-agreement__divider large-margin-bottom large-margin-top" />
              <button
                className="button link xl-margin-bottom"
                onClick={onReturnClick.bind(null, returnUrl)}
              >
                <Icon name="ei-chevron-left" /> Return to your offer
              </button>
            </div>
            <OrderedListItem index={1}>{signerNameInputs}</OrderedListItem>
            <OrderedListItem index={2}>
              <h6 className="prose">Select HOA Status</h6>
              <div className="text-product-medium">
                Is your home part of an HOA (Homeowner's Association)
              </div>
              <div className="od-purchase-agreement__input">
                <RadioButtonGroupInput
                  onChange={value => {
                    this.props.onUpdate('hasHoa', value === 'yes');
                  }}
                  options={[
                    {
                      inputName: 'hasHoa',
                      isChecked: hasHoa,
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      inputName: 'hasHoa',
                      isChecked: hasHoa === false,
                      label: 'No',
                      value: 'no',
                    },
                  ]}
                  verticallyAligned={true}
                />
              </div>
              {hoaInputs}
            </OrderedListItem>
            <OrderedListItem index={3}>
              <h6 className="prose">Select a close date</h6>
              <div className="text-product-medium">Let us know your preferred close date</div>
              <div className="od-purchase-agreement__input">
                <DatePicker
                  date={closeDate && closeDate.toDate()}
                  initialVisibleMonth={moment()
                    .add(MIN_DAYS_TO_CLOSE + 1, 'd')
                    .toDate()}
                  isDayBlocked={this.isInvalidCloseDate}
                  onDateChange={this.handleUpdate}
                  placeholder="Select date"
                />
              </div>
            </OrderedListItem>
          </div>
        </div>
      </section>
    );
  }

  // add input line (signer names + hoa fees)
  private addListInput = (name, baseValue) => {
    this.props.onUpdate(name, [...this.props[name], baseValue]);
  };

  // remove input line ""
  private removeListInput = name => {
    this.props.onUpdate(name, this.props[name].slice(0, -1));
  };

  // replace item at index of list of values with the new value
  private onListUpdate = (baseName, index, newValue) => {
    const originalList = this.props[baseName];

    this.props.onUpdate(baseName, [
      ...originalList.slice(0, index),
      newValue,
      ...originalList.slice(index + 1),
    ]);
  };

  // parses <list attribute name>-<index> name and updates the targeted item with new value
  private onListItemUpdate = (name, value) => {
    const [baseName, index] = name.split('-');
    this.onListUpdate(baseName, index, value);
  };

  // "" except it updates a property value within an object within a list
  // e.g. given 'someAttr-0', 'b', 4 we may expect the following
  // someAttr: [{a: 1, b: 2}, {a: 2, b: 3}] ==> [{a: 1, b: 4}, {a: 2, b: 3}]
  private onListObjectUpdate = (baseName, name, value) => {
    const [propertyName, index] = name.split('-');
    const originalList = this.props[baseName];
    const listObject = originalList[index];

    this.onListUpdate(baseName, index, {
      ...listObject,
      [propertyName]: value,
    });
  };

  private handleUpdate = date => {
    const momentDate = moment(date);
    this.props.onUpdate('closeDate', momentDate);
  };

  private isInvalidCloseDate = date => {
    const momentDate = moment(date);
    return momentDate.isBefore(MIN_DAYS_CLOSE_DATE) || momentDate.isAfter(MAX_DAYS_CLOSE_DATE);
  };
}

export default PurchaseAgreement;
